import type { TranslateFunction } from '@locales/generated';
import { useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { useCardSizeLabels } from '@apps/card/utils/cardSizeName';
import { mapToLabel, useFacetOptions } from './components/Filters/facetOptions';
import { buildPresentedFacets } from './components/Filters/presentFacets';
import type { FilterFacet } from './components/Filters/Filter.types';
import { COLOR_FACET, FACET_ORDER, STYLE_FACET } from './components/Filters/Filters.constants';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import type { StationeryTemplate } from '@apps/card/types/stationeryTemplate.types';
import type { TemplateFacetIndex } from './templateFacetMatching.types';
import { countFacetOptions, groupSelections } from './templateFacetMatching';
import { usePrecomputedEmbeddings } from '@shared/utils/search';

const FACET_POSITION = FACET_ORDER as ReadonlyArray<string>;

/**
 * The facets the gallery presents, with per-option counts scoped to the current selection
 * (see `countFacetOptions`). Must run inside `DesignGalleryFiltersProvider`.
 */
export const useFilterFacets = (templates: ReadonlyArray<StationeryTemplate>, facetIndex: TemplateFacetIndex) => {
  const { t, tq } = useTranslation('stationery');
  const { themes, keywordGroups, loadFailed: themesLoadFailed } = usePrecomputedEmbeddings();
  // Style and Color option labels are derived from the index too, so an option the
  // locale map has never heard of still names itself.
  const { labelByOption } = useFacetOptions();
  const { activePrintFilters } = useDesignGalleryFiltersContext();
  const tFilterOptions = t('dashboard', 'designsGallery', 'filterOptions') as Record<string, TranslateFunction>;

  // Labels for the Feature group and its options are code-owned (`tq`) — the `filterOptions`
  // locale map is externally maintained and can't take additions from this change. These tq
  // calls must sit lexically inside this hook (the translate extractor cannot trace tq through
  // function parameters). Code labels take precedence over the locale map so "Foil" / "Photo"
  // read like the nav rather than the older "With Foil" / "With Photo". Orientation options
  // keep their locale labels (Portrait / Landscape / Square).
  const featureFacetLabel = tq`Feature`;
  const freeFacetLabel = tq`Free`;
  const priceBasicLabel = tq`Free`;
  const formatDigitalLabel = tq`Digital`;
  const photoWithLabel = tq`Photo`;
  const foilWithLabel = tq`Foil`;
  // The blessed product name rather than a local string, as the Size facet used before it.
  const { postcard: sizePostcardLabel } = useCardSizeLabels();
  const productMagnetLabel = tq`Magnet`;
  const styleFacetLabel = tq`Style`;

  return useMemo(() => {
    const localFacetLabels: Record<string, string> = {
      feature: featureFacetLabel,
      free: freeFacetLabel,
      'price basic': priceBasicLabel,
      'format digital': formatDigitalLabel,
      'photo with': photoWithLabel,
      'foil with': foilWithLabel,
      'size postcard': sizePostcardLabel,
      'product magnet': productMagnetLabel,
      // Style options are the index's keyword groups (`keywordGroups` in themes.json). A group the
      // bundle adds before it is listed here shows its raw key (see the option resolver below).
      'style bold': tq`Bold`,
      'style botanical': tq`Botanical`,
      'style classic': tq`Classic`,
      'style cultural': tq`Cultural`,
      'style destination': tq`Destination`,
      'style elegant': tq`Elegant`,
      'style floral': tq`Floral`,
      'style metallic': tq`Metallic`,
      'style minimal': tq`Minimal`,
      'style modern': tq`Modern`,
      'style rustic': tq`Rustic`,
      'style seasonal': tq`Seasonal`,
      'style textured': tq`Textured`,
      'style typographic': tq`Typographic`,
      'style vintage': tq`Vintage`,
      'style whimsical': tq`Whimsical`,
    };

    // Same index as the filter, so the counts describe exactly what selecting an option would show.
    const facetOptionCounts = countFacetOptions(templates, groupSelections(activePrintFilters), facetIndex);

    const facets = buildPresentedFacets(
      facetOptionCounts,
      {
        facet: groupCode => localFacetLabels[groupCode] ?? tFilterOptions[groupCode]?.() ?? `[${groupCode}]`,
        option: compositeCode => {
          const translationKey = compositeCode.replace(':', ' '); // Don't use colon as separator, i18next will think it's a namespace
          // An unlisted style group has no index-derived label either; its raw key names it.
          const styleGroupKey = compositeCode.startsWith(`${STYLE_FACET}:`) ? compositeCode.slice(STYLE_FACET.length + 1) : undefined;
          return localFacetLabels[translationKey] ?? tFilterOptions[translationKey]?.() ?? mapToLabel(compositeCode, labelByOption) ?? styleGroupKey;
        },
      },
      activePrintFilters,
    );

    // The Style rail offers the index's keyword groups — a curated, closed set — and nothing else.
    // Templates also carry per-keyword options (`style:botanical`) so the nav's deep links keep
    // filtering (`styleOptionsForTemplate`); those are hidden here unless selected, which keeps a
    // deep-linked selection visible and uncheckable. This replaced the 1% rarity floor, which
    // only existed to prune the raw search vocabulary.
    const styleFacetIndex = facets.findIndex(facet => facet.groupCode === STYLE_FACET);
    if (styleFacetIndex !== -1) {
      const styleFacet = facets[styleFacetIndex];
      styleFacet.options = styleFacet.options.filter(
        option => Object.prototype.hasOwnProperty.call(keywordGroups, option.compositeCode.slice(STYLE_FACET.length + 1)) || activePrintFilters.has(option.compositeCode),
      );
      if (!styleFacet.options.length) facets.splice(styleFacetIndex, 1);
    }

    // Style is counted from the embeddings index, which only the client loads, and it leads the
    // row. While the index loads a placeholder pill holds its place — so the server-rendered public
    // gallery (PRNT-5494) and its hydration agree, and no gallery sees the row shove along when the
    // facet appears. A failed load drops it: there is nothing to fill it with.
    // Color is in the same position as Style: it now comes from the index alone — the `tags` column
    // that used to supply it server-side is gone — so it needs the same placeholder.
    if (!themes && !themesLoadFailed) {
      const pendingLabels: ReadonlyArray<readonly [string, string]> = [
        [STYLE_FACET, tFilterOptions[STYLE_FACET]?.() ?? styleFacetLabel],
        // `color` has a locale entry of long standing (the facet used to be served), so it needs
        // no code-owned string of its own.
        [COLOR_FACET, tFilterOptions[COLOR_FACET]?.() ?? `[${COLOR_FACET}]`],
      ];
      for (const [groupCode, label] of pendingLabels) {
        if (facets.some(facet => facet.groupCode === groupCode)) continue;
        const pending: FilterFacet = { groupCode, label, options: [], pending: true };
        // `buildPresentedFacets` emits in `FACET_ORDER`; the placeholder takes its place in it.
        const at = facets.findIndex(facet => FACET_POSITION.indexOf(facet.groupCode) > FACET_POSITION.indexOf(groupCode));
        facets.splice(at === -1 ? facets.length : at, 0, pending);
      }
    }

    return facets;
  }, [
    tFilterOptions,
    templates,
    facetIndex,
    themes,
    keywordGroups,
    themesLoadFailed,
    labelByOption,
    activePrintFilters,
    featureFacetLabel,
    freeFacetLabel,
    priceBasicLabel,
    formatDigitalLabel,
    photoWithLabel,
    foilWithLabel,
    sizePostcardLabel,
    productMagnetLabel,
    styleFacetLabel,
    tq,
  ]);
};
